/* The client state model — ported VERBATIM from src/client/app.ts (the
   ClientState interface, Me, UserWithGrants, thisMonth, landingViewIn, and the
   initial state object). This is the CF10 big-bang React shell: the same field
   set the vanilla client held in its single mutable `state`, so every renderer
   and action reasons about exactly the same shape. Do NOT add or drop a field
   without matching the vanilla source — parity is the contract. */
import type { FeatureId } from "../../../shared/features";
import type { Team } from "../../../shared/teams";
import type { Project, RecordState, RoleNeed } from "../../../shared/projects";
import type { Role } from "../../../shared/roles";
import type { PdpCriterion, PdpDeliverable, PdpGate } from "../../../shared/pdp";
import type { Product, Vehicle } from "../../../shared/products";
import type { ProductPdp, SlotKey, SlotState } from "../../../shared/product-pdp";
import type { FeedReading, FeedRef, FeedSourceInfo } from "../../../shared/feeds";
import type { PdpPhase } from "../../../shared/pdp";
import type { TimeEntryLink } from "../../../shared/time-entry";
import type {
  AppUser,
  DerivedRole,
  OrganizerRole,
  RoleGrant,
  UserRef,
} from "../../../shared/organizer-roles";
import type { Directory } from "../../../shared/directories";
import { type Surface } from "../../../shared/features";

export interface Me {
  user: UserRef | null;
  roles: string[];
  /* the ids of the teams you are the recorded manager of (P20) — which team,
     not just that you manage one */
  manages: string[];
  /* which features this deployment shows (feature-surface.md FS4) — learned
     from /api/me before any data fetch; "release" until then, fail closed */
  surface: Surface;
}

/* firstSeen is absent for roster people who have never signed in (R11);
   derived roles come from the roster, read-only (R12) */
export type UserWithGrants = UserRef & {
  firstSeen?: AppUser["firstSeen"];
  grants: RoleGrant[];
  derived?: DerivedRole[];
};

/* One feed ref's reading as the page holds it (kpi-feed-seam.md KF12/KF16):
   the LOGGED reading from GET /api/feeds/reading or the answer of the row's
   own POST /api/feeds/read, plus the page-session facts the cell shows — a
   read in flight, the client clock at a Retry's answer, and the HTTP layer's
   failure sentence (the feed's own failure is the reading's unreachable
   state, never an error here). */
export interface FeedRowReading {
  reading: FeedReading;
  inFlight: boolean;
  retriedAt: string | null;
  httpError: string | null;
}

/* two slots on one ref share one log (KF11), so the page keys by the ref */
export const feedKey = (ref: FeedRef): string => `${ref.sourceId}/${ref.measure}`;

export interface ClientState {
  view:
    | "home"
    | "intake"
    | "projects"
    | "project-form"
    | "teams"
    /* the Admin-only team list (teams-and-products.md TP8) — "teams" above
       is the Entra org chart, shown as Org */
    | "team-admin"
    | "permissions"
    | "roles"
    | "pdp"
    | "products"
    | "about-hermes"
    | "issues";
  /* which About Hermes tab is open (help-layer.md HL4) */
  aboutTab: "how" | "who";
  menuOpen: boolean;
  /* the "Report a problem or idea" form is open (issue-tracker.md IT9) —
     shell chrome, reachable from every page */
  reportOpen: boolean;
  /* which menu on the section's bar is dropped open, by label — only ever
     one at a time (section-navigator N13) */
  openMenu: string | null;
  /* which area page is showing, by tab label; null = the record list */
  area: string | null;
  openProjectId: string | null;
  openTeamId: string | null;
  /* which folders are unfolded in the picker's tree, by view label (N22) */
  expandedFolders: string[];
  /* the Open picker — the file-explorer overlay of the section's views (N10) */
  pickerOpen: boolean;
    /* the picker's search box — presentation only, never a query (N31). */
  pickerSearch: string;
  /* the month "now" means, "YYYY-MM" (monthly-allocations.md M7) */
  currentMonth: string;
  teams: Team[];
  projects: Project[];
  roles: Role[];
  /* who is signed in and what they may do (organizer-roles.md R7) */
  me: Me;
  users: UserWithGrants[];
  organizerRoles: OrganizerRole[];
  /* May this actor add to the role catalog (canonical-roles CR8)? */
  catalogAuthority: boolean;
  /* the Add-a-role form's own fields */
  roleQuery: string;
  roleDescription: string;
  roleCardinality: Role["cardinality"];
  /* which role's inline editor is open, by id — null = none (CR12) */
  editingRoleId: string | null;
  editRoleName: string;
  editRoleDescription: string;
  editRoleCardinality: Role["cardinality"];
  /* projects a saved tightening contradicts (CR15) */
  roleConflicts: string[];
  /* the PDP catalog (pdp-catalog.md PC16): one fetch of the three lists is
     one fault domain; the tab and form state stay in the view */
  pdp: { deliverables: PdpDeliverable[]; criteria: PdpCriterion[]; gates: PdpGate[] };
  pdpPending: boolean;
  pdpError: boolean;
  /* the Products section (products-pdp.md PP15): the product list (the
     intake vocabulary, read-only here), one PDP record per product that has
     anything recorded, and the catalog above — read together on navigation
     as one fault domain. Derivations (owed set, next gate) run in the views
     from these plus `pdp`. */
  openProductId: string | null;
  products: Product[];
  /* every product's vehicles (teams-and-products.md TP4), read with the
     products in the same fault domain */
  vehicles: Vehicle[];
  productPdp: ProductPdp[];
  productsPending: boolean;
  productsError: boolean;
  /* the change-phase form replaces the heading's action row in place */
  phaseFormOpen: boolean;
  /* the one product form open on the section (products-manage.md PM4):
     New product… on the home, Rename… on the record's heading */
  productFormOpen: "new" | "rename" | null;  /* "rename" = Market & vehicles… (TP3) */
  /* the section home's filters — views over loaded data, never a query */
  productsSearch: string;
  productsShowRetired: boolean;
  /* the Deliverables area's PP16 filters, reset when a product opens or closes */
  deliverablesPhase: PdpPhase | "all";
  deliverablesState: SlotState | "all";
  deliverablesSearch: string;
  /* the slot whose Record evidence drawer is open (Screen 4), or none */
  evidenceDraft: SlotKey | null;
  /* the gate review sheet is open on the Phase area (Screen 5) */
  reviewDraft: boolean;
  /* the gate the open sheet decides when it is NOT the declared phase's own
     exit: a correction of an earlier gate's in-force review (PP10, opened
     from its Gate history row); null = the declared phase's gate */
  reviewGateId: string | null;
  /* the certificate page open over the Phase area (Screen 6), by id */
  openCertificateId: string | null;
  /* GET /api/feeds/sources, read best-effort with the section — [] when the
     route is absent (release surface) so the feed arm refuses client-side */
  feedSources: FeedSourceInfo[];
  /* the open product's feed readings by `feedKey` — loaded with the record,
     replaced by the row's Read / Refresh / Retry (KF12) */
  feedReadings: Record<string, FeedRowReading>;
  /* the Permissions search filter (R9) — presentation only */
  userSearch: string;
  /* the reader navigated somewhere themselves — the landing derivation (MA2) */
  navigated: boolean;
  /* the self fetch failed — costs the My Work page alone (MA11/OH6) */
  myError: boolean;
  /* OH6: every load group carries a pending flag BESIDE its error flag */
  loadPending: boolean;
  teamsPending: boolean;
  myPending: boolean;
  dirsPending: boolean;
  mePending: boolean;
  usersPending: boolean;
  /* configured external links for SAP time entry (charge-codes C4) */
  timeEntryLinks: TimeEntryLink[];
  /* item being renamed in the form, or null when adding a new one */
  editingItemId: string | null;
  /* project being edited in the form, or null when adding a new one */
  editingId: string | null;
  formError: string | null;
  /* archive/delete failures, shown on the list views */
  listError: string | null;
  /* a save is in flight — extra Save clicks are ignored (no duplicates) */
  saving: boolean;
  /* the store data failed — nothing on any view is truthful without it */
  loadError: boolean;
  /* the teams source failed — costs the Teams view only (OH6) */
  teamsError: boolean;
  /* the folders records file into (designs/directories.md DIR1) */
  directories: Directory[];
  /* the directories load failed — the picker says so instead of a tree */
  dirsError: boolean;
  /* Permissions' own read failed (PR #55 review) */
  usersError: boolean;
  /* the one selection (DIR6) */
  selectedDirId: string | null;
  /* which directory folds are open, by id; "" is the Not filed group */
  expandedDirs: string[];
  /* the picker's inline New folder form (DIR4) */
  newFolderOpen: boolean;
  newFolderError: string | null;
  /* Resourcing's inline demand editor (N34 / PN10) */
  roleDraft: RoleNeed[] | null;
  /* Which role the Resourcing chart is narrowed to; null = whole project (PN14) */
  trendRole: string | null;
  /* The demand editor's current role, or null = the list is showing (PN22) */
  demandRole: string | null;
  roleSearch: string;
  /* The Roster's month range (charter-and-roster.md RC9) */
  rosterFrom: string | null;
  rosterTo: string | null;
  /* the chart view's "Show as table" toggle (RC12/RC22) */
  rosterAsTable: boolean;
  rosterError: string | null;
  /* The demand chart's window when the reader has chosen one (RC30) */
  trendFrom: string | null;
  trendTo: string | null;
  trendRangeError: string | null;
  /* the Charter editor's inline reason, verbatim from validateProjectInput (RC1) */
  charterError: string | null;
  /* the Charter reads at rest; Edit charter opens the form (charter-form CH8) */
  charterEditing: boolean;
  /* ── the persistent records table (navigation-redesign.md NR8, dev surface):
     its filters are VIEWS over loaded data — state chip, folder-rail selection
     ("all" | "" for Not filed | a directory id), search, group lens, and the
     column sort. Reset on section changes: folders are per-section. ── */
  tableState: "all" | RecordState;
  tableDir: string;
  tableQuery: string;
  tableGroup: "folder" | "state";
  tableSort: "name" | "state";
  tableSortAsc: boolean;
}

export const thisMonth = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

/* What the app opens on (designs/shared-home.md SH5): the shared Home, unless
   THIS BROWSER remembered one of Home's doors and that section is on this
   surface. Anything else remembered — a stale or hand-edited value — is Home.
   (My Work was the derived landing until the Sep 14 MVP cut; Projects was
   the landing outright from then until Home.) */
export const START_DOORS = ["intake", "projects"] as const;
export type StartDoor = (typeof START_DOORS)[number];

export const landingView = (
  remembered: string | null,
  on: (id?: FeatureId) => boolean,
): ClientState["view"] => {
  if (remembered === "intake" && on("intake")) return "intake";
  if (remembered === "projects") return "projects";
  return "home";
};

/* The remembered start (SH4): a per-viewer convenience in browser storage,
   never the server's. Every access is guarded — a private window or blocked
   site data reads as "nothing remembered" and lands on Home. */
export const START_VIEW_KEY = "hermes.startView";

export const readStartView = (): StartDoor | null => {
  try {
    const v = localStorage.getItem(START_VIEW_KEY);
    return (START_DOORS as readonly string[]).includes(v ?? "") ? (v as StartDoor) : null;
  } catch {
    return null;
  }
};

export const writeStartView = (door: StartDoor | null): void => {
  try {
    if (door === null) localStorage.removeItem(START_VIEW_KEY);
    else localStorage.setItem(START_VIEW_KEY, door);
  } catch {
    /* storage unavailable — the choice simply is not remembered */
  }
};

/* A FRESH initial state per app mount (a function, not a shared singleton, so
   two mounts — or a test remount — never share one mutable object). Before
   /api/me answers, the view is the derivation's fallback branch. */
export const initialState = (): ClientState => ({
  aboutTab: "how",
  view: "home",
  menuOpen: false,
  reportOpen: false,
  openMenu: null,
  area: null,
  openProjectId: null,
  openTeamId: null,
  expandedFolders: [],
  pickerOpen: false,
  pickerSearch: "",
  currentMonth: thisMonth(),
  teams: [],
  projects: [],
  roles: [],
  me: { user: null, roles: [], manages: [], surface: "release" },
  users: [],
  organizerRoles: [],
  catalogAuthority: false,
  roleQuery: "",
  roleDescription: "",
  roleCardinality: "many",
  editingRoleId: null,
  editRoleName: "",
  editRoleDescription: "",
  editRoleCardinality: "many",
  roleConflicts: [],
  pdp: { deliverables: [], criteria: [], gates: [] },
  pdpPending: false,
  pdpError: false,
  openProductId: null,
  products: [],
  vehicles: [],
  productPdp: [],
  productsPending: false,
  productsError: false,
  phaseFormOpen: false,
  productFormOpen: null,
  productsSearch: "",
  productsShowRetired: false,
  deliverablesPhase: "all",
  deliverablesState: "all",
  deliverablesSearch: "",
  evidenceDraft: null,
  reviewDraft: false,
  reviewGateId: null,
  openCertificateId: null,
  feedSources: [],
  feedReadings: {},
  userSearch: "",
  navigated: false,
  loadPending: false,
  teamsPending: false,
  myPending: false,
  dirsPending: false,
  mePending: false,
  usersPending: false,
  myError: false,
  timeEntryLinks: [],
  editingItemId: null,
  editingId: null,
  formError: null,
  listError: null,
  saving: false,
  loadError: false,
  teamsError: false,
  directories: [],
  dirsError: false,
  usersError: false,
  selectedDirId: null,
  expandedDirs: [],
  newFolderOpen: false,
  newFolderError: null,
  roleDraft: null,
  trendRole: null,
  demandRole: null,
  roleSearch: "",
  trendFrom: null,
  trendTo: null,
  trendRangeError: null,
  rosterFrom: null,
  rosterTo: null,
  rosterAsTable: false,
  rosterError: null,
  charterError: null,
  charterEditing: false,
  tableState: "all",
  tableDir: "all",
  tableQuery: "",
  tableGroup: "folder",
  tableSort: "name",
  tableSortAsc: true,
});
